import React, { useEffect, useState, ReactNode } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin, AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { ReactInsightsLogger, ReactInsightsLoggerInterface } from '../services/ReactInsightsLogger';
import { ReactInsightsConfig } from '../config/config-bridge';

export const reactPlugin = new ReactPlugin();

interface ApplicationInsightsContextValue {
    logger: ReactInsightsLoggerInterface | null;
    appInsights: ApplicationInsights | null;
    isInitialized: boolean;
}

interface ApplicationInsightsProviderProps {
    children: ReactNode;
    config?: ReactInsightsConfig;
}

export const ApplicationInsightsProvider: React.FC<ApplicationInsightsProviderProps> = ({
    children,
    config
}) => {
    const [contextValue, setContextValue] = useState<ApplicationInsightsContextValue>({
        logger: null,
        appInsights: null,
        isInitialized: false
    });

    useEffect(() => {
        let appInsights: ApplicationInsights | null = null;
        let logger: ReactInsightsLoggerInterface | null = null;

        // Create our own instance if we have config
        if (config && config.connectionString) {

            appInsights = new ApplicationInsights({
                config: {
                    connectionString: config.connectionString,
                    extensions: [reactPlugin],
                    disableAjaxTracking: false,
                    autoTrackPageVisitTime: true,
                    enableCorsCorrelation: true,
                    correlationHeaderExcludedDomains: ['vimeo.com'],
                    enableRequestHeaderTracking: true,
                    enableResponseHeaderTracking: true,
                    // SDK-INTERNAL diagnostics only — this does NOT gate the telemetry you
                    // send. 0 turns those internal messages off; see the logging skill.
                    loggingLevelTelemetry: 0,
                }
            });

            appInsights.loadAppInsights();

            // Set cloud role name
            appInsights.addTelemetryInitializer((envelope) => {
                if (envelope.tags) {
                    envelope.tags['ai.cloud.role'] = config.cloudRoleName;
                }
            });

            // Set user context if available
            // TODO: currently always undefined
            if (config.userContext) {
                appInsights.context.user.authenticatedId = config.userContext.authenticatedId;
                appInsights.context.user.accountId = config.userContext.accountId;
            }

            logger = new ReactInsightsLogger(appInsights, reactPlugin);
        }
        // No connection string configured: a logger with no client is a silent no-op — every
        // ReactInsightsLogger method guards on `this.appInsights`, so nothing is sent AND nothing
        // is printed.
        //
        // ⚠️ This does NOT match the Angular side, despite an earlier comment here saying so.
        // There, a missing PCP_TELEMETRY_SINK stops telemetry but `writeToConsole` still runs, so a
        // developer keeps the full console stream. Here they get silence. That is a real gap in this
        // parallel React stack — which also has no severity floors, no domain scoping, no
        // browser_session_id and no noise filtering — and it wants its own change to close.
        else {
            logger = new ReactInsightsLogger(null, null);
        }

        setContextValue({
            logger,
            appInsights,
            isInitialized: true
        });

        // Cleanup function
        return () => {
            if (appInsights) {
                appInsights.flush();
            }
        };
    }, [config]);

    return (
        <AppInsightsContext.Provider value={reactPlugin}>
            <AIContextProvider value={contextValue}>
                {children}
            </AIContextProvider>
        </AppInsightsContext.Provider>
    );
};

// Internal context for our custom logger and state
const AIContext = React.createContext<ApplicationInsightsContextValue | undefined>(undefined);

const AIContextProvider: React.FC<{ value: ApplicationInsightsContextValue; children: ReactNode }> = ({ value, children }) => (
    <AIContext.Provider value={value}>{children}</AIContext.Provider>
);

export const useApplicationInsightsContext = (): ApplicationInsightsContextValue => {
    const context = React.useContext(AIContext);
    if (context === undefined) {
        throw new Error('useApplicationInsightsContext must be used within an ApplicationInsightsProvider');
    }
    return context;
};
